import { useState } from 'react';
import { Eye, EyeOff, KeyRound, LogIn, Mail } from 'lucide-react';
import InnyMascot from '../components/InnyMascot';
import InnyWordmark from '../components/InnyWordmark';
import { APP_VERSION } from '../lib/version';
import axiosInstance from '../utils/axiosInstance';
import { errorMessage } from '../lib/api';

export default function OdooLogin({ onSignedIn }) {
  const [login, setLogin] = useState(() => {
    try {
      return localStorage.getItem('inny.lastLogin') || '';
    } catch {
      return '';
    }
  });
  const [secret, setSecret] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (busy || !login.trim() || !secret) return;
    setBusy(true);
    setError('');
    try {
      const r = await axiosInstance.post('api/receiving/auth/login/', { login: login.trim(), secret });
      try {
        localStorage.setItem('inny.lastLogin', login.trim());
      } catch {
        /* ignore */
      }
      setSecret('');
      onSignedIn?.(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || errorMessage(err, 'Sign in failed'));
      setBusy(false);
    }
  };

  return (
    <div className="safe-t safe-b flex min-h-screen flex-col justify-between px-6 py-8">
      <div className="mt-[8vh] [@media(max-height:700px)]:mt-0">
        <div className="[@media(max-height:700px)]:hidden">
          <InnyMascot size={140} animated className="-ml-3" />
        </div>
        <p className="mt-1 leading-none"><InnyWordmark size={56} /></p>
        <p className="mt-2 max-w-xs text-[16px] text-ink2">Sign in with your Odoo account. What you receive is booked in your name.</p>
        <p className="mt-2 text-[12px] text-ink2">Inny {APP_VERSION}</p>
      </div>
      <form onSubmit={submit} className="mx-auto w-full max-w-sm" autoComplete="on">
        {error && <p className="mb-3 rounded-xl bg-alert-bg px-3 py-2 text-[14px] text-alert">{error}</p>}
        <label className="block">
          <span className="text-[12px] font-semibold text-ink2">Odoo login</span>
          <span className="mt-0.5 flex h-13 items-center gap-2 rounded-xl border border-line bg-surface px-3 focus-within:border-ink" style={{ height: 52 }}>
            <Mail className="h-5 w-5 shrink-0 text-ink2" />
            <input
              value={login}
              onChange={(e) => setLogin(e.target.value)}
              type="email"
              name="username"
              autoComplete="username"
              autoCapitalize="off"
              autoFocus={!login}
              placeholder="you@lamptwist.be"
              className="min-w-0 flex-1 bg-transparent text-[16px] outline-none placeholder:text-ink2/90"
            />
          </span>
        </label>
        <label className="mt-3 block">
          <span className="text-[12px] font-semibold text-ink2">Password or API key</span>
          <span className="mt-0.5 flex items-center gap-2 rounded-xl border border-line bg-surface px-3 focus-within:border-ink" style={{ height: 52 }}>
            <KeyRound className="h-5 w-5 shrink-0 text-ink2" />
            <input
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              type={show ? 'text' : 'password'}
              name="password"
              autoComplete="current-password"
              autoFocus={Boolean(login)}
              className="min-w-0 flex-1 bg-transparent text-[16px] outline-none"
            />
            <button type="button" onClick={() => setShow((v) => !v)} aria-label={show ? 'Hide' : 'Show'} className="flex h-9 w-9 items-center justify-center rounded-full text-ink2 active:bg-sunken">
              {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </span>
        </label>
        <button type="submit" disabled={busy || !login.trim() || !secret} className="mt-4 flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-ink text-[16px] font-semibold text-bg active:opacity-80 disabled:opacity-40">
          <LogIn className="h-5 w-5" /> {busy ? 'Checking with Odoo…' : 'Sign in'}
        </button>
        <p className="mt-3 text-center text-[13px] text-ink2">
          Two-factor authentication on? Use an API key: Odoo → Preferences → Account security. The device stays signed in for 12 hours.
        </p>
      </form>
    </div>
  );
}
